import { useState } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import Avatar from "@mui/material/Avatar";
import MenuItem from "@mui/material/MenuItem";
import ImageIcon from "@mui/icons-material/Image";
import Box from "@mui/material/Box";
import CloudDownload from "@mui/icons-material/CloudDownload";
import SearchIcon from "@mui/icons-material/Search";
import { useMediaQuery } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import ExercisesBarChart from "@nualang/nualang-ui-components/dist/Charts/Bar/Exercises";
import TimeLineLoaded from "@nualang/nualang-ui-components/dist/Lists/Timeline/TimelineLoaded";
import ResponsiveTabs from "@nualang/nualang-ui-components/dist/Navigation/ResponsiveTabs";
import ShareButton from "@nualang/nualang-ui-components/dist/Misc/ShareButton";
import Button from "@nualang/nualang-ui-components/dist/Misc/DefaultColourButton";
import UpgradeSubscription from "@nualang/nualang-ui-components/dist/Dialogs/UpgradeSubscription/UpgradeSubscription";
import MedalSelector from "@nualang/nualang-ui-components/dist/Misc/MedalSelector/MedalSelector";
import NualangVerifiedIcon from "@nualang/nualang-ui-components/dist/Misc/NualangVerifiedIcon/NualangVerifiedIcon";
import { DateTime } from "luxon";
const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  avatar: {
    height: 40,
    width: 40,
    marginRight: theme.spacing(2),
  },
  header: {
    display: "flex",
    alignItems: "start",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(),
  },
  headerText: {
    flexGrow: 1,
  },
  buttons: {
    padding: theme.spacing(1),
    justifyContent: "space-between",
    textAlign: "left",
    "@media print": {
      display: "none",
    },
  },
  avatarHeader: {
    height: 40,
    width: 40,
    marginRight: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1, 1, 1, 0),
  },
}));

export default function Activity({
  t = (text) => text,
  completions = [],
  completionsTotal,
  progress = [],
  progressTotal,
  handleViewEvent,
  picture,
  title,
  description,
  filterChange,
  isCreator,
  fromDateChange,
  toDateChange,
  toDate,
  fromDate,
  disableHashParams = false,
  subscription,
  name,
  userImage,
  isCompletionsLoading,
  isProgressLoading,
  mainSiteUrl,
  verificationStatus,
  gameAward,
}) {
  const { classes } = useStyles();
  const [limit, setLimit] = useState(5);
  const [filter, setFilter] = useState("");
  const [customFilter, setCustomFilter] = useState("last7Days");
  const [errorHelperTextToDate, setErrorHelperTextToDate] = useState("");
  const [errorHelperTextFromDate, setErrorHelperTextFromDate] = useState("");
  const [showTable, setShowTable] = useState(false);
  const isMedScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const handleFromDateChange = (date, context) => {
    if (context?.validationError) {
      setErrorHelperTextFromDate(t("invalid_date"));
    } else if (errorHelperTextFromDate !== "") {
      setErrorHelperTextFromDate("");
    }
    fromDateChange(date);
  };
  const handleChange = (event, newAlignment) => {
    setShowTable(newAlignment);
  };
  const handleToDateChange = (date, context) => {
    if (context?.validationError) {
      setErrorHelperTextToDate(t("invalid_date"));
    } else if (errorHelperTextToDate !== "") {
      setErrorHelperTextToDate("");
    }
    toDateChange(date);
  };
  const handleCustomFromChange = (value) => {
    setCustomFilter(value);
    let newFromDate;
    const currentDate = DateTime.now();

    switch (value) {
      case "last7Days":
        newFromDate = currentDate.minus({ days: 7 });
        break;
      case "last28Days":
        newFromDate = currentDate.minus({ days: 28 });
        break;
      case "last90Days":
        newFromDate = currentDate.minus({ days: 90 });
        break;
      case "last12Weeks":
        newFromDate = currentDate.minus({ weeks: 12 });
        break;
      case "last12Months":
        newFromDate = currentDate.minus({ months: 12 });
        break;
      case "customDays":
        return;
      default:
        break;
    }
    fromDateChange(newFromDate);
    toDateChange(currentDate);
  };

  const handleFilterChange = () => {
    filterChange(filter);
  };

  function handleLoadMore() {
    setLimit((prevLimit) => {
      const newLimit = prevLimit + 5;
      return newLimit;
    });
  }

  function setExcelXlsxHeader(item) {
    let arrSheet = [];
    const label = t("attempt_info");
    const nowSheet = DateTime.now().toFormat("f");
    const title = `${label.toUpperCase()} - ${t(
      item.exercise,
    ).toUpperCase()}, ${nowSheet}`;
    arrSheet.push([title]);
    arrSheet.push([""]);
    arrSheet.push([t("exercise_info").toUpperCase()]);
    arrSheet.push([t("topic").toUpperCase() + ":", item.topicName]);
    arrSheet.push([
      t("description").toUpperCase() + ":",
      description || item.description || "",
    ]);
    arrSheet.push([t("exercise").toUpperCase() + ":", item.exercise]);
    arrSheet.push([
      t("name").toUpperCase() + ":",
      item.roleplayName || item.botName,
    ]);
    arrSheet.push([""]);
    arrSheet.push([t("records").toUpperCase()]);

    const timeSpent = item.createdAt - item.exerciseStartTime;
    const timeCreateAt = DateTime.fromMillis(item.createdAt);
    const timeStartTime = DateTime.fromMillis(item.exerciseStartTime);
    const attemptTime = DateTime.fromMillis(item.exerciseStartTime).toFormat(
      "HH':'mm':'ss",
    );
    const attemptDate = DateTime.fromMillis(item.exerciseStartTime).toFormat(
      "dd LLL yyyy",
    );
    arrSheet.push([
      t("name").toUpperCase() + ":",
      item.preferred_username || "",
    ]);
    arrSheet.push([
      t("time_spent").toUpperCase() + ":",
      timeSpent && timeSpent > 0 && timeCreateAt.diff(timeStartTime),
    ]);
    arrSheet.push([t("date").toUpperCase() + ":", attemptDate || ""]);
    arrSheet.push([t("time").toUpperCase() + ":", attemptTime || ""]);
    arrSheet.push([""]);

    if (item.exercise !== "pronunciation") {
      arrSheet.push([t("answer_method").toUpperCase()]);
      const attemptMap = new Map();
      const methods = (item.answerAttempts || item.messages || []).filter(
        (a) => a.method,
      );
      methods.map((attempt) => {
        attemptMap.set(
          attempt.method,
          attemptMap.get(attempt.method)
            ? attemptMap.get(attempt.method) + 1
            : 1,
        );
        return false;
      });

      const attempLabels = Array.from(attemptMap.keys()).map((l) => t(l));
      const attemptData = Array.from(attemptMap.values());
      attempLabels.map((itemLabel, i) =>
        arrSheet.push([itemLabel, attemptData[i]]),
      );
      arrSheet.push([""]);
    }
    return arrSheet;
  }

  function setExcelXlsxAttemptTable(arrSheet, item) {
    if (item.exercise === "bot" || item.exercise === "bot-listen-and-speak")
      return arrSheet;

    if (item && (!item.answerAttempts || !item.answerAttempts.length)) {
      return arrSheet;
    }

    arrSheet.push([t("attempt_table").toUpperCase()]);

    const head = [
      t("result").toUpperCase(),
      t("attempt").toUpperCase(),
      t("answer").toUpperCase(),
    ];

    if (item.exercise === "roleplay") {
      head.push(t("question").toUpperCase());
      head.push(t("type").toUpperCase());
    } else if (item.exercise !== "pronunciation") {
      head.push(t("method").toUpperCase());
    }
    arrSheet.push(head);

    let correctTotal = 0;
    let incorrectTotal = 0;
    let skipped = 0;

    for (let i = 0; i < item.answerAttempts.length; i++) {
      const attemptItem = item.answerAttempts[i];
      switch (attemptItem.result) {
        case "correct":
          correctTotal += 1;
          break;
        case "incorrect":
          incorrectTotal += 1;
          break;
        case "skipped":
          skipped += 1;
          break;

        default:
          break;
      }

      const body = [
        attemptItem.result ? t(attemptItem.result) : "",
        attemptItem.attempt ? attemptItem.attempt : "",
        attemptItem.answer ? attemptItem.answer : "",
      ];

      if (item.exercise === "roleplay") {
        body.push(attemptItem?.question ? attemptItem.question : "");
        body.push(attemptItem.type ? attemptItem.type : "");
      } else if (item.exercise !== "pronunciation") {
        body.push(attemptItem.method ? attemptItem.method : "");
      }
      arrSheet.push(body);
    }
    arrSheet.push([""]); // blank line

    // TOTAL
    arrSheet.push([t("answers").toUpperCase()]);
    arrSheet.push([t("correct"), t("incorrect"), t("skipped")]);
    arrSheet.push([correctTotal, incorrectTotal, skipped]);
    arrSheet.push([""]); // blank line

    return arrSheet;
  }
  function handleDownloadAsExcelXlsxOverview(XLSX, workbook) {
    let actor1Played = 0;
    let actor2Played = 0;
    let actor1,
      actor2 = {};
    if (completions[0] && completions[0].actor1) {
      actor1 = completions[0].actor1;
    }
    if (completions[0] && completions[0].actor2) {
      actor2 = completions[0].actor2;
    }
    completions.forEach((ev) => {
      if (!ev.selectedActor || ev.selectedActor === 1) {
        actor1Played += 1;
      } else if (ev.selectedActor && ev.selectedActor === 2) {
        actor2Played += 1;
      }
    });

    const answerAttempts = completions.reduce(function (result, e) {
      if (e.answerAttempts && e.answerAttempts.length) {
        result = [...result, ...e.answerAttempts];
      } else if (e.messages && e.messages.length) {
        result = [...result, ...e.messages];
      }
      return result;
    }, []);

    let correctTotal = 0;
    let incorrectTotal = 0;
    let skipped = 0;

    answerAttempts.forEach((el) => {
      switch (el.result) {
        case "correct":
          correctTotal += 1;
          break;
        case "incorrect":
          incorrectTotal += 1;
          break;
        case "skipped":
          skipped += 1;
          break;

        default:
          break;
      }
    });

    const attemptMap = new Map();
    const methods = (answerAttempts || []).filter((a) => a && a.method);
    methods.map((attempt) => {
      attemptMap.set(
        attempt.method,
        attemptMap.get(attempt.method) ? attemptMap.get(attempt.method) + 1 : 1,
      );
      return false;
    });

    const timeSpent = completions.reduce(function (result, e) {
      const timeCreateAt = DateTime.fromMillis(e.createdAt);
      const timeStartTime = DateTime.fromMillis(e.exerciseStartTime);
      if (e.exerciseStartTime && e.createdAt) {
        result += e.createdAt - e.exerciseStartTime;
      }
      return { result, timeCreateAt, timeStartTime };
    }, 0);

    let arrSheet = [];
    const label = t("attempt_info").toUpperCase();
    const timeNow = DateTime.now().toFormat("f");
    const title = `${label.toUpperCase()} - ${t(
      "overview",
    ).toUpperCase()}, ${timeNow}`;
    arrSheet.push([title]);
    arrSheet.push([t("description").toUpperCase() + ":", description || ""]);
    arrSheet.push([""]);
    arrSheet.push([
      t("time_spent").toUpperCase() + ":",
      timeSpent.result &&
        timeSpent.result > 0 &&
        timeSpent.timeCreateAt
          .diff(timeSpent.timeStartTime, "minutes")
          .toFormat("HH':'mm':'ss"),
    ]);
    arrSheet.push([""]);

    if ((actor1 && actor1.name) || (actor2 && actor2.name)) {
      arrSheet.push([t("role_played").toUpperCase()]);
      if (actor1 && actor1.name) {
        arrSheet.push(["", actor1.name + ":", actor1Played]);
      }
      if (actor2 && actor2.name) {
        arrSheet.push(["", actor2.name + ":", actor2Played]);
      }
      arrSheet.push([""]);
    }

    arrSheet.push([t("answer_method").toUpperCase()]);
    const attempLabels = Array.from(attemptMap.keys()).map((l) => t(l));
    const attemptData = Array.from(attemptMap.values());
    attempLabels.map((itemLabel, i) =>
      arrSheet.push(["", itemLabel + ":", attemptData[i]]),
    );
    arrSheet.push([""]);
    arrSheet.push([t("answers").toUpperCase() + ":"]);
    arrSheet.push(["", t("correct") + ":", correctTotal]);
    arrSheet.push(["", t("incorrect") + ":", incorrectTotal]);
    arrSheet.push(["", t("skipped") + ":", skipped]);
    arrSheet.push([""]);

    arrSheet.push([t("exercises_completed").toUpperCase()]);
    const exerciseMap = new Map([
      [t("pronunciation"), 0],
      [t("listening"), 0],
      [t("translation"), 0],
      [t("roleplay"), 0],
      [t("bot"), 0],
    ]);
    completions.map((completion) => {
      if (completion.exercise === "listener") {
        exerciseMap.set(t("listening"), exerciseMap.get(t("listening")) + 1);
      } else if (completion.exercise === "pronouncer") {
        exerciseMap.set(
          t("pronunciation"),
          exerciseMap.get(t("pronunciation")) + 1,
        );
      } else if (completion.exercise === "bot-listen-and-speak") {
        exerciseMap.set(t("bot"), exerciseMap.get(t("bot")) + 1);
      } else {
        exerciseMap.set(
          t(completion.exercise),
          exerciseMap.get(t(completion.exercise)) + 1,
        );
      }
      return false;
    });
    const labelsExerCompleted = Array.from(exerciseMap.keys());
    const mapExerCompleted = Array.from(exerciseMap.values());
    for (let i = 0; i < 5; i++) {
      arrSheet.push(["", labelsExerCompleted[i] + ":", mapExerCompleted[i]]);
    }
    arrSheet.push([""]);

    //WEEK----------
    arrSheet.push([t("exercises_completed").toUpperCase() + ":"]);
    const dayNumToDayName = {
      0: t("sun"),
      1: t("mon"),
      2: t("tue"),
      3: t("wed"),
      4: t("thu"),
      5: t("fri"),
      6: t("sat"),
    };
    const completionsThisWeek = completions.filter(
      (completion) =>
        DateTime.fromMillis(completion.createdAt).weekNumber ===
        DateTime.now().weekNumber,
    );
    const exerciseMapWeek = new Map([
      [t("sun"), 0],
      [t("mon"), 0],
      [t("tue"), 0],
      [t("wed"), 0],
      [t("thu"), 0],
      [t("fri"), 0],
      [t("sat"), 0],
    ]);
    completionsThisWeek.map((completion) => {
      exerciseMapWeek.set(
        dayNumToDayName[DateTime.fromMillis(completion.createdAt).weekday],
        exerciseMapWeek.get(
          dayNumToDayName[DateTime.fromMillis(completion.createdAt).weekday],
        ) + 1,
      );
      return false;
    });

    const labelsExerCompletedWeek = Array.from(exerciseMapWeek.keys());
    const mapExerCompletedWeek = Array.from(exerciseMapWeek.values());
    for (let i = 0; i < 7; i++) {
      arrSheet.push([
        "",
        labelsExerCompletedWeek[i] + ":",
        mapExerCompletedWeek[i],
      ]);
    }
    arrSheet.push([""]);

    let worksheet = XLSX.utils.aoa_to_sheet(arrSheet);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Overview");
    return workbook;
  }

  function handleDownloadAsExcelXlsxRoleplay(XLSX, workbook, item, id) {
    let arrSheet = setExcelXlsxHeader(item); // HEADER
    if (item.selectedActor === 1) {
      arrSheet.push([
        t("role_played").toUpperCase() + ":",
        item.actor1 && item.actor1.name ? item.actor1.name : "-",
      ]);
    } else if (item.selectedActor === 2) {
      arrSheet.push([
        t("role_played").toUpperCase() + ":",
        item.actor2 && item.actor2.name ? item.actor2.name : "-",
      ]);
    }
    arrSheet.push([""]); // Line

    arrSheet = setExcelXlsxAttemptTable(arrSheet, item);
    let worksheet = XLSX.utils.aoa_to_sheet(arrSheet);
    const sheetName = `Roleplay:${id},${item.preferred_username}`.substr(0, 30);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    return workbook;
  }
  function handleDownloadAsExcelXlsxTranslation(XLSX, workbook, item, id) {
    let arrSheet = setExcelXlsxHeader(item); // HEADER
    arrSheet.push([""]); // Line
    arrSheet = setExcelXlsxAttemptTable(arrSheet, item); // AttemptTable

    let worksheet = XLSX.utils.aoa_to_sheet(arrSheet);
    const sheetName = `Translation:${id},${item.preferred_username}`.substr(
      0,
      30,
    );
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    return workbook;
  }
  function handleDownloadAsExcelXlsxListening(XLSX, workbook, item, id) {
    let arrSheet = setExcelXlsxHeader(item); // HEADER
    arrSheet.push([""]); // Line
    arrSheet = setExcelXlsxAttemptTable(arrSheet, item); // AttemptTable

    let worksheet = XLSX.utils.aoa_to_sheet(arrSheet);
    const sheetName = `Listening:${id},${item.preferred_username}`.substr(
      0,
      30,
    );
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    return workbook;
  }
  function handleDownloadAsExcelXlsxPronunciation(XLSX, workbook, item, id) {
    let arrSheet = setExcelXlsxHeader(item); // HEADER
    arrSheet.push([""]); // Line
    arrSheet = setExcelXlsxAttemptTable(arrSheet, item); // AttemptTable

    let worksheet = XLSX.utils.aoa_to_sheet(arrSheet);
    const sheetName = `Pronunciation:${id},${item.preferred_username}`.substr(
      0,
      30,
    );
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    return workbook;
  }
  function handleDownloadAsExcelXlsxBot(XLSX, workbook, item, id) {
    const arrSheet = setExcelXlsxHeader(item); // HEADER
    arrSheet.push([""]); // Line

    if (item.messages && item.messages.length > 0) {
      arrSheet.push([t("attempt_messages").toUpperCase()]);

      arrSheet.push([
        t("datetime").toUpperCase(),
        t("name").toUpperCase(),
        t("text").toUpperCase(),
      ]);

      item.messages.map((item) =>
        arrSheet.push([item.datetime, item.name, item.text]),
      );
    }

    arrSheet.push([""]); // Line

    let worksheet = XLSX.utils.aoa_to_sheet(arrSheet);
    const sheetName = `Bot:${id},${item.preferred_username}`.substr(0, 30);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    return workbook;
  }

  function handleDownloadAsExcelXlsx() {
    const XLSX = require("xlsx");
    let workbook = XLSX.utils.book_new();
    workbook = handleDownloadAsExcelXlsxOverview(XLSX, workbook);

    for (let i = 0; i < completions.length; i++) {
      const eventItem = completions[i];
      switch (eventItem.exercise) {
        case "roleplay":
          workbook = handleDownloadAsExcelXlsxRoleplay(
            XLSX,
            workbook,
            eventItem,
            i,
          );
          break;
        case "translation":
          workbook = handleDownloadAsExcelXlsxTranslation(
            XLSX,
            workbook,
            eventItem,
            i,
          );
          break;
        case "listening":
        case "listener":
          workbook = handleDownloadAsExcelXlsxListening(
            XLSX,
            workbook,
            eventItem,
            i,
          );
          break;
        case "pronunciation":
        case "pronouncer":
          workbook = handleDownloadAsExcelXlsxPronunciation(
            XLSX,
            workbook,
            eventItem,
            i,
          );
          break;
        case "bot":
        case "bot-listen-and-speak":
          workbook = handleDownloadAsExcelXlsxBot(XLSX, workbook, eventItem, i);
          break;
        default:
          console.error("Exercise not found");
          break;
      }
    }
    /* generate XLSX file and send to client */
    const nowSheet = DateTime.now().toFormat("f");
    const fileName = `attempts_${nowSheet}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }

  const [isUpgradeSubscriptionOpen, setIsUpgradeSubscriptionOpen] =
    useState(false);
  // const handleOpenUpgradeSubscriptionModal = () => {
  //   setIsUpgradeSubscriptionOpen(true);
  // }
  const handleCloseUpgradeSubcriptionModal = () => {
    setIsUpgradeSubscriptionOpen(false);
  };
  const handleSubscriptionPage = () => {
    window.open(`${mainSiteUrl}/pricing/app`, "_blank");
  };

  const tabs = [
    {
      label: `${t("completed")} (${completions.length})`,
      id: "Completed",
    },
    {
      label: `${t("in_progress_activity")} (${progress.length})`,
      id: "In-Progress",
    },
  ];

  let initialTabValue;
  if (window.location.hash) {
    const selectedTab = window.location.hash;
    initialTabValue = tabs.findIndex(
      (tab) => tab.id === selectedTab.replace("#", ""),
    );
  } else {
    initialTabValue = completions.length === 0 && progress.length > 0 ? 1 : 0;
  }

  return (
    <div className={classes.root}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        {userImage ? (
          <div className={classes.header}>
            <div
              style={{
                position: "relative",
                display: "inline-block",
              }}
            >
              <Box>
                <Avatar src={userImage} alt='' />
                {verificationStatus === "approved" && (
                  <NualangVerifiedIcon
                    sx={{
                      bottom: "-10px",
                      right: "-6px",
                      fontSize: 21,
                    }}
                  />
                )}
                {gameAward === "bronze" &&
                  verificationStatus !== "approved" && (
                    <MedalSelector
                      color={"bronze"}
                      width={20}
                      fade={false}
                      backgroundColor={true}
                      sx={{
                        position: "absolute",
                        bottom: "-10px",
                        right: "-6px",
                        borderRadius: "50%",
                        padding: "1px",
                      }}
                    />
                  )}
                {gameAward === "silver" &&
                  verificationStatus !== "approved" && (
                    <MedalSelector
                      color={"silver"}
                      width={20}
                      fade={false}
                      backgroundColor={true}
                      sx={{
                        position: "absolute",
                        bottom: "-8px",
                        right: "-6px",
                        borderRadius: "50%",
                        padding: "1px",
                      }}
                    />
                  )}
                {gameAward === "gold" && verificationStatus !== "approved" && (
                  <MedalSelector
                    color={"gold"}
                    width={20}
                    fade={false}
                    backgroundColor={true}
                    sx={{
                      position: "absolute",
                      bottom: "-8px",
                      right: "-6px",
                      borderRadius: "50%",
                      padding: "1px",
                    }}
                  />
                )}
              </Box>
            </div>
            <Box
              sx={{
                marginLeft: 2,
                marginTop: 'auto',
                marginBottom: 'auto'
              }}>
              <Typography
                component='h1'
                sx={{
                  fontWeight: 'bold',
                  fontSize: 19
                }}>
                {name}
              </Typography>
            </Box>
          </div>
        ) : null}
        {(picture || title || description) && (
          <div className={classes.header}>
            {picture ? (
              <Avatar className={classes.avatarHeader} src={`${picture}`} />
            ) : (
              <Avatar className={classes.avatarHeader}>
                <ImageIcon />
              </Avatar>
            )}
            <div className={classes.headerText}>
              <Typography variant='h5' component='h2'>
                {title}
              </Typography>
              <Typography variant='body2' color='textSecondary' gutterBottom>
                {description}
              </Typography>
              <div className={classes.buttons}>
                <ShareButton
                  t={t}
                  url={window.location.href}
                  title={"share"}
                  className={classes.button}
                  subscription={subscription}
                  mainSiteUrl={mainSiteUrl}
                />
                <Button
                  data-cy='activity-button-download'
                  className={classes.button}
                  variant='contained'
                  startIcon={<CloudDownload />}
                  size='small'
                  onClick={() => handleDownloadAsExcelXlsx()}
                >
                  {t("download_csv_file")}
                </Button>
              </div>
            </div>
          </div>
        )}
        <Grid container sx={{ width: "100%" }} spacing={2}>
          {isCreator ? (
            <Grid
              style={{ marginTop: "-29px", marginBottom: "10px" }}
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <TextField
                id='filter'
                name='filter'
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                type='text'
                label={t("search_for_student")}
                variant='outlined'
                sx={{ mt: isMedScreen ? 0 : "41.6px" }}
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={handleFilterChange}
                          size='large'
                          aria-label='Search'
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                }}
              />
            </Grid>
          ) : null}

          <Grid
            style={{ marginTop: "5px", marginBottom: "10px" }}
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <Box
              component='fieldset'
              sx={{
                borderWidth: 0,
              }}
            >
              <TextField
                select
                id='graphChoice'
                name='graphChoice'
                // onChange={changeFilter}
                margin='dense'
                variant='outlined'
                value={customFilter}
                size='small'
                onChange={(e) => handleCustomFromChange(e.target.value)}
                label='Page-Wide Filter'
                fullWidth
                slotProps={{
                  input: {
                    style: {
                      height: "55px",
                    },
                  },

                  inputLabel: {
                    sx: {
                      "&.Mui-focused": {
                        marginTop: 0,
                      },
                      marginTop: customFilter ? 0 : "7px",
                    },
                  }
                }}>
                <MenuItem value={"last7Days"}>Last 7 days</MenuItem>
                <MenuItem value={"last28Days"}>Last 28 days</MenuItem>
                <MenuItem value={"last90Days"}>Last 90 days</MenuItem>
                <MenuItem value={"last12Weeks"}>Last 12 weeks</MenuItem>
                <MenuItem value={"last12Months"}>Last 12 months</MenuItem>
                <MenuItem value={"customDays"}>Custom</MenuItem>
              </TextField>
            </Box>
          </Grid>
          {customFilter === "customDays" ? (
            <Grid
              container
              spacing={2}
              style={{
                marginBottom: "20px",
                marginLeft: "4px",
              }}
            >
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Box
                  component='fieldset'
                  sx={{
                    borderWidth: 0,
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    gutterBottom
                    component='legend'
                    sx={{
                      color: 'inherit',
                      paddingBottom: 1
                    }}>
                    {t("select_the_from_date")}
                  </Typography>
                  <DateTimePicker
                    id='date-picker-dialog-ini'
                    label={t("from")}
                    format='MM/dd/yyyy hh:mm'
                    inputVariant='outlined'
                    maxDate={toDate}
                    value={fromDate}
                    onChange={handleFromDateChange}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: errorHelperTextFromDate,
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Box
                  component='fieldset'
                  sx={{
                    borderWidth: 0,
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    gutterBottom
                    component='legend'
                    sx={{
                      color: 'inherit',
                      paddingBottom: 1
                    }}>
                    {t("select_the_to_date")}
                  </Typography>
                  <DateTimePicker
                    id='date-picker-dialog-end'
                    label={t("to")}
                    format='MM/dd/yyyy hh:mm'
                    inputVariant='outlined'
                    value={toDate}
                    maxDate={DateTime.now()}
                    onChange={handleToDateChange}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: errorHelperTextToDate,
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          ) : null}
          <br />
          <Grid container sx={{ width: "100%" }} spacing={3}>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <Card sx={{ marginLeft: "15px" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography gutterBottom variant='h5' component='h2'>
                      {t("exercises")}
                    </Typography>
                    <ToggleButtonGroup
                      color='primary'
                      value={showTable}
                      exclusive
                      onChange={handleChange}
                      aria-label='Platform'
                      sx={{ marginLeft: "auto", height: "30px" }}
                    >
                      <ToggleButton value={false}>{t("chart")}</ToggleButton>
                      <ToggleButton value={true}>{t("table")}</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <ExercisesBarChart
                    t={t}
                    completions={completions}
                    alt='Exercises Chart'
                    showTable={showTable}
                    aria-label='Exercise completion chart'
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <br />
        <ResponsiveTabs
          t={t}
          tabs={tabs}
          initialValue={initialTabValue}
          disableHashParams={disableHashParams}
        >
          <Box sx={{
            py: 2
          }}>
            <TimeLineLoaded
              t={t}
              events={(completions || []).slice(0, limit)}
              total={completionsTotal}
              handleLoadMore={handleLoadMore}
              handleViewEvent={handleViewEvent}
              isLoading={isCompletionsLoading}
            />
          </Box>
          <Box sx={{
            py: 2
          }}>
            <TimeLineLoaded
              t={t}
              events={(progress || []).slice(0, limit)}
              total={progressTotal}
              handleLoadMore={handleLoadMore}
              labelKey='exercise_in_progress'
              isLoading={isProgressLoading}
            />
          </Box>
        </ResponsiveTabs>
        <UpgradeSubscription
          key={"UpgradeSubscription"}
          t={t}
          open={isUpgradeSubscriptionOpen}
          handleClose={handleCloseUpgradeSubcriptionModal}
          onConfirm={handleSubscriptionPage}
          subscription={subscription}
        />
      </LocalizationProvider>
    </div>
  );
}
